import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/axios';
import { Globe, ArrowRight, AlertCircle, AlertTriangle } from 'lucide-react';
import { formatDate, formatDateTime } from '@/lib/utils';

/**
 * Online-Anmeldungen Conversion-Tracker (Phase D2 Bonus).
 *
 * Zeigt Counts der letzten 30 Tage + Funnel (eingegangen → bestätigt → genehmigt)
 * + Sparkline der täglichen Eingänge. Klick führt zur Inbox.
 */
export default function RegistrationsTrackerWidget() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [lastRun, setLastRun] = useState(null); // neuester Scheduler-Run oder null

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [statsR, runsR] = await Promise.all([
          api.get('/admin/registrations/stats?window_days=30'),
          api.get('/admin/registrations/scheduler-runs?limit=1').catch(() => ({ data: [] })),
        ]);
        if (!cancelled) {
          setStats(statsR.data);
          setLastRun(runsR.data?.[0] || null);
        }
      } catch (e) {
        if (!cancelled) setHidden(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (hidden) return null;
  if (loading || !stats) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 animate-pulse">
        <div className="h-4 w-48 bg-slate-100 rounded mb-3" />
        <div className="h-16 bg-slate-50 rounded" />
      </div>
    );
  }

  // Wenn in 30 Tagen NICHTS gekommen ist UND nichts in Bearbeitung — Widget ausblenden
  if (stats.total === 0 && stats.pending === 0) return null;

  const decided = stats.confirmed + stats.approved + stats.rejected;
  const pendingDecision = stats.confirmed; // bestätigt, noch nicht entschieden
  const hasOpen = pendingDecision > 0;

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-5 mb-6"
      data-testid="registrations-tracker-widget"
    >
      <SchedulerHealthStrip lastRun={lastRun} />

      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand-red/10 flex items-center justify-center">
            <Globe className="h-5 w-5 text-brand-red" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-brand-anthracite">Online-Anmeldungen</h3>
            <p className="text-sm text-brand-muted">Letzte {stats.window_days} Tage</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/registrations-inbox')}
          className="text-sm text-brand-red hover:text-brand-red/80 inline-flex items-center gap-1"
          data-testid="registrations-tracker-open"
        >
          Zur Inbox <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiCell label="Eingegangen" value={stats.total} accent="slate" />
        <KpiCell label="Bestätigt" value={stats.approved + stats.rejected + stats.confirmed} accent="brand"
                 hint={stats.total > 0 ? `${Math.round(stats.confirmation_rate * 100)} %` : null} />
        <KpiCell label="Genehmigt" value={stats.approved} accent="emerald"
                 hint={(stats.approved + stats.rejected) > 0
                   ? `${Math.round(stats.approval_rate * 100)} %`
                   : null} />
        <KpiCell
          label={hasOpen ? 'Wartet auf Entscheidung' : 'In Bearbeitung'}
          value={pendingDecision || stats.pending}
          accent={hasOpen ? 'brand' : 'slate'}
          icon={hasOpen ? AlertCircle : null}
          actionable={hasOpen}
          onClick={hasOpen ? () => navigate('/registrations-inbox') : undefined}
        />
      </div>

      {/* Sparkline */}
      {stats.daily && stats.daily.length > 0 && <Sparkline data={stats.daily} />}
    </div>
  );
}

function KpiCell({ label, value, accent = 'slate', hint, icon: Icon, actionable, onClick }) {
  const accents = {
    slate: 'text-brand-anthracite',
    brand: 'text-brand-red',
    indigo: 'text-brand-red',
    emerald: 'text-brand-success',
  };
  const Wrapper = actionable ? 'button' : 'div';
  const wrapperProps = actionable
    ? { onClick, type: 'button', className: 'text-left p-3 rounded-lg bg-brand-canvas hover:bg-brand-red/5 border border-transparent hover:border-brand-red/30 transition-colors' }
    : { className: 'p-3 rounded-lg bg-brand-canvas' };
  return (
    <Wrapper {...wrapperProps} data-testid={`registrations-tracker-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-xs text-brand-muted flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className={`text-2xl font-bold ${accents[accent] || accents.slate}`}>
        {value.toLocaleString('de-DE')}
      </div>
      {hint && <div className="text-xs text-brand-muted">{hint}</div>}
    </Wrapper>
  );
}

function Sparkline({ data }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="flex items-end gap-0.5 h-12" data-testid="registrations-tracker-sparkline">
      {data.map((d) => {
        const h = Math.round((d.count / max) * 100);
        return (
          <div
            key={d.date}
            className="flex-1 bg-brand-red/20 rounded-t hover:bg-brand-red/40 transition-colors"
            style={{ height: `${Math.max(4, h)}%` }}
            title={`${d.date}: ${d.count}`}
          />
        );
      })}
    </div>
  );
}

/**
 * Warnkachel: zeigt nur dann etwas, wenn der letzte Scheduler-Lauf
 * fehlgeschlagen ist ODER länger als 3 Kalendertage zurückliegt.
 * Wenn noch nie gelaufen → nichts (Widget ist ja neu).
 */
function SchedulerHealthStrip({ lastRun }) {
  if (!lastRun) return null;
  const startedAt = new Date(lastRun.started_at);
  const ageMs = Date.now() - startedAt.getTime();
  const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24));
  const failed = lastRun.status === 'failed';
  const stale = lastRun.job_name === 'sla_daily' && ageDays >= 3;
  if (!failed && !stale) return null;

  const tone = failed
    ? 'bg-red-50 border-red-200 text-red-800'
    : 'bg-brand-red/5 border-brand-red/20 text-brand-red';
  const Icon = failed ? AlertTriangle : AlertCircle;
  const label = failed
    ? `Letzter Daily-Job fehlgeschlagen am ${formatDateTime(startedAt)}${lastRun.error_message ? ` — ${lastRun.error_message}` : ''}`
    : `Daily-Job lief zuletzt vor ${ageDays} Tag(en) (${formatDate(startedAt)}). Bitte Scheduler prüfen.`;

  return (
    <div
      className={`flex items-center gap-2 text-sm px-3 py-2 mb-4 rounded-lg border ${tone}`}
      data-testid="scheduler-health-warning"
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      <a
        href="/settings"
        className="text-xs underline whitespace-nowrap"
        data-testid="scheduler-health-open-settings"
      >
        Zu Einstellungen
      </a>
    </div>
  );
}
